import { GetCoopSummaryParams, MessageRepository, MessageService } from '@aipb/gulliver/personal-content/data-access';
import { CoopUserWithOnline } from '@aipb/gulliver/personal-content/model';
import { AuthService } from '@aipb/shared/auth';
import { InsideLoadingComponent, LoadingProgressComponent } from '@aipb/shared/ui/loading';
import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSidenavModule } from '@angular/material/sidenav';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, interval, map, Observable, startWith, tap } from 'rxjs';
import {
  MessageCollectionListComponent,
  MessageFilterComponent,
  MessageListComponent,
  MessageUserListComponent
} from '../../components';
import { OpenCollectionUseCase } from '../../usecases';
import { MatIconModule } from '@angular/material/icon';
import { MtxButtonModule } from '@ng-matero/extensions/button';

@UntilDestroy()
@Component({
  selector: 'pc-message-list-container',
  standalone: true,
  imports: [
    CommonModule,
    MessageListComponent,
    MatSidenavModule,
    MatIconModule,
    MatButtonModule,
    MatExpansionModule,
    TranslocoModule,
    MessageCollectionListComponent,
    MessageUserListComponent,
    MessageFilterComponent,
    InsideLoadingComponent,
    LoadingProgressComponent,
    MatFormFieldModule,
    MatInputModule,
    MtxButtonModule
  ],
  templateUrl: './message-list-container.component.html',
  styleUrls: ['./message-list-container.component.scss'],
  providers: [MessageRepository, MessageService]
})
export class MessageListContainerComponent implements OnInit {
  @Input() collectionId: string;
  @Input() showMessageInput = false;
  @Input() showFilters = true;

  private messageRepository = inject(MessageRepository);
  messages$ = this.messageRepository.messages$;
  onlineUsers$ = this.messageRepository.onlineUsers$;
  filters$ = this.messageRepository.filters$;
  activeFilters$ = this.messageRepository.activeFilters$;
  loading$ = this.messageRepository.loading$;
  coopSummary$ = this.messageRepository.coopSummary$;
  users$: Observable<CoopUserWithOnline[]> = combineLatest([
    this.messageRepository.onlineUsers$,
    this.messageRepository.coopSummary$
  ]).pipe(
    map(([ou, coopSummary]) => {
      const users: CoopUserWithOnline[] = (coopSummary?.users || []).map((u) => ({
        ...u,
        online: false
      }));
      const onlineUsers: CoopUserWithOnline[] = ou.map((ms) => ({
        userId: ms.id,
        collections: [],
        avatar: ms.avatar,
        name: ms.name,
        project: '',
        online: true
      }));

      const allUsers = [...users, ...onlineUsers];

      return [...new Map(allUsers.map((user) => [user.userId, user])).values()].sort(
        (a, b) => Number(b.online) - Number(a.online)
      );
    })
  );
  private messageService = inject(MessageService);
  private transloco = inject(TranslocoService);
  lang$ = this.transloco.langChanges$.pipe(startWith(this.transloco.getActiveLang()));
  private auth = inject(AuthService);
  userId = this.auth.getUserId();
  private lastFilters: Record<string, string[]> = {};
  private openCollection = inject(OpenCollectionUseCase);

  constructor() {
    interval(15000)
      .pipe(
        tap(() => {
          this.onRefresh();
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  ngOnInit() {
    const params: GetCoopSummaryParams = {};

    if (this.collectionId) {
      params.collectionUniqueId = this.collectionId;
    }

    this.messageService.getCoopSummary(params).subscribe();

    this.onFilter({});
  }

  onRefresh() {
    this.onFilter(this.lastFilters);
  }

  onFilter(filters: Record<string, string[]>) {
    if (this.collectionId) {
      filters = {
        ...filters,
        collection_id: [this.collectionId]
      };
    }

    this.lastFilters = filters;
    this.messageRepository.setActiveFilters(filters);
    this.messageService.fetchMessages(filters).subscribe();
  }

  onSendMessageToUser(userId: string) {
    this.messageService
      .sendMessageToUserDialog(userId)
      .pipe(tap(() => this.onRefresh()))
      .subscribe();
  }

  onSendMessageToCollection(message: string) {
    this.messageService
      .sendMessageToCollection(message, this.collectionId)
      .pipe(tap(() => this.onRefresh()))
      .subscribe();
  }

  onSendMessageToCollectionDialog(uniqueId: string) {
    this.messageService
      .sendMessageToCollectionDialog(uniqueId)
      .pipe(tap(() => this.onRefresh()))
      .subscribe();
  }

  onOpenCollection(id: string) {
    this.openCollection.execute({
      id
    });
  }

  onOpenCollectionInTab(id: string) {
    this.openCollection.execute({
      id,
      openInNewTab: true
    });
  }
}
